<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useConfig } from 'dashboard/composables/useConfig';
import FunnelAPI from '../../../../api/funnel';
import StageColorPicker from './StageColorPicker.vue';
import draggable from 'vuedraggable';
import agents from '../../../../api/agents';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import { reactive } from 'vue';

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
  initialData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();
const store = useStore();
const { isStacklab } = useConfig();
const loading = ref(false);
const errorMessage = ref('');

const formData = ref({
  name: props.initialData.name || '',
  description: props.initialData.description || '',
  stages: {},
  agents: props.initialData.agents || [],
  global_custom_attributes: Array.isArray(
    props.initialData.global_custom_attributes
  )
    ? props.initialData.global_custom_attributes.map(attr => ({
        name: attr.name || '',
        type: attr.type || '',
      }))
    : [],
});

const stages = ref([]);
const newStage = ref({
  name: '',
  color: '#FF6B6B',
  description: '',
  auto_create_conditions: [],
});

const editingStage = ref(null);

const dragOptions = {
  animation: 150,
  ghostClass: 'ghost-card',
};

const vFocus = {
  mounted: el => el.focus(),
};

const agentsList = ref([]);
const loadingAgents = ref(false);
const existingStageIds = ref(new Set());

// Refs para controle de condições
const conditionsDropdownOpen = ref(false);
const conditionsSelectorRef = ref(null);
const newCondition = ref({
  type: 'contact_has_tag',
  value: '',
  attribute: '',
  operator: 'equal_to',
});

// Gera o ID baseado no nome
const generateId = name => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// Gera um ID único apenas se necessário
const generateUniqueId = (baseName, existingStages, currentId = null) => {
  // Se já tem um ID e está editando, mantém o mesmo
  if (currentId && editingStage.value) {
    return currentId;
  }

  const baseId = generateId(baseName);
  const stageIds = existingStages.map(stage => stage.id);

  // Se o ID base não existe, usa ele
  if (!stageIds.includes(baseId)) {
    return baseId;
  }

  // Se existe, adiciona um timestamp para garantir unicidade
  return `${baseId}_${Date.now()}`;
};

// Atualiza o ID apenas para novas etapas
watch(
  () => newStage.value.name,
  newName => {
    if (newName && !editingStage.value) {
      newStage.value.id = generateId(newName);
    }
  }
);

// Converte as etapas para o formato esperado pela API
const formatStagesForAPI = () => {
  const formattedStages = {};

  // Garantir que as posições reflitam a ordem atual
  stages.value.forEach((stage, index) => {
    // Definir explicitamente a posição baseada no índice atual
    const position = index + 1;

    // Pega os templates existentes da etapa atual
    const existingTemplates =
      props.initialData?.stages?.[stage.id]?.message_templates || [];

    formattedStages[stage.id] = {
      name: stage.name,
      color: stage.color,
      position: position, // Garantir que a posição reflita a ordem atual
      description: stage.description,
      auto_create_conditions: stage.auto_create_conditions || [],
      message_templates: existingTemplates, // Mantém os templates existentes
    };

    // Debug da posição definida
    console.log(`Formatando para API: ${stage.name} -> posição ${position}`);
  });

  return formattedStages;
};

// Função para lidar com a reordenação das etapas
const handleOrderChange = async () => {
  if (!props.isEditing) return;

  try {
    // Atualizar as posições com base na nova ordem
    stages.value.forEach((stage, index) => {
      stage.position = index + 1;
      console.log(
        `Atualizando posição: ${stage.name} -> posição ${stage.position}`
      );
    });

    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      stages: formatStagesForAPI(),
    };

    console.log('Payload de ordenação:', payload);

    // Atualiza o estado local sem fazer chamada à API
    props.initialData.stages = payload.stages;
  } catch (error) {
    console.error('Erro ao atualizar ordem:', error);
  }
};

const handleAddStage = e => {
  if (e?.target?.closest('.colorpicker--chrome')) {
    return;
  }

  if (!newStage.value.name) return;

  if (editingStage.value) {
    const index = stages.value.findIndex(s => s.id === editingStage.value.id);
    if (index !== -1) {
      stages.value[index] = {
        ...editingStage.value,
        name: newStage.value.name,
        color: newStage.value.color,
        description: newStage.value.description,
        auto_create_conditions: newStage.value.auto_create_conditions,
        id: editingStage.value.id,
      };
      if (props.isEditing) {
        handleOrderChange();
      }
    }
    editingStage.value = null;
  } else {
    let stageId = generateId(newStage.value.name);
    let counter = 1;
    while (existingStageIds.value.has(stageId)) {
      stageId = `${generateId(newStage.value.name)}_${counter}`;
      counter++;
    }
    stages.value.push({
      ...newStage.value,
      id: stageId,
    });
    existingStageIds.value.add(stageId);
  }
  newStage.value = {
    name: '',
    color: '#FF6B6B',
    description: '',
    auto_create_conditions: [],
  };
};

const removeStage = index => {
  stages.value.splice(index, 1);
};

// Busca todos os funis e coleta os IDs existentes
const fetchExistingStageIds = async () => {
  try {
    const { data: allFunnels } = await FunnelAPI.get();
    existingStageIds.value.clear();

    allFunnels.forEach(funnel => {
      if (funnel.id !== props.initialData?.id) {
        // Ignora o funil atual se estiver editando
        Object.keys(funnel.stages || {}).forEach(id => {
          existingStageIds.value.add(id);
        });
      }
    });
  } catch (error) {
    console.error('Erro ao carregar IDs existentes:', error);
  }
};

// Inicializa as etapas se estiver editando
onMounted(() => {
  console.log('FunnelForm mounted, initialData:', props.initialData);

  if (props.isEditing || props.initialData) {
    console.log('Inicializando form com dados:', props.initialData);

    formData.value = {
      name: props.initialData.name || '',
      description: props.initialData.description || '',
      stages: props.initialData.stages || {},
      agents: props.initialData.agents || [],
      global_custom_attributes: Array.isArray(
        props.initialData.global_custom_attributes
      )
        ? props.initialData.global_custom_attributes.map(attr => ({
            name: attr.name || '',
            type: attr.type || '',
          }))
        : [{ name: '', type: '' }],
    };

    console.log('formData atualizado:', formData.value);

    // Inicializa as etapas do template
    if (props.initialData.stages) {
      console.log('Inicializando etapas:', props.initialData.stages);

      stages.value = Object.entries(props.initialData.stages)
        .map(([id, stage]) => {
          const mappedStage = {
            id,
            name: stage.name,
            color: stage.color,
            description: stage.description,
            position: parseInt(stage.position, 10) || 0,
            auto_create_conditions: stage.auto_create_conditions || [],
          };
          console.log('Etapa mapeada:', mappedStage);
          return mappedStage;
        })
        .sort((a, b) => a.position - b.position);

      console.log(
        'Etapas inicializadas e ordenadas por posição:',
        stages.value
      );
    }
  }

  fetchAgents();

  if (
    !Array.isArray(formData.value.global_custom_attributes) ||
    formData.value.global_custom_attributes.length === 0
  ) {
    formData.value.global_custom_attributes = [{ name: '', type: '' }];
  }
});

const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    const { data } = await agents.get();
    // Se tiver agentes pré-selecionados e eles não estiverem na lista, adiciona-os
    if (props.initialData?.agents?.length) {
      props.initialData.agents.forEach(agent => {
        if (!data.find(a => a.id === agent.id)) {
          data.push(agent);
        }
      });
    }
    agentsList.value = data;
  } catch (error) {
    console.error('Erro ao carregar agentes:', error);
  } finally {
    loadingAgents.value = false;
  }
};

// Funções para gerenciar condições
const addCondition = () => {
  if (!newCondition.value.value) return;

  const condition = { ...newCondition.value };
  newStage.value.auto_create_conditions.push(condition);

  // Reset do formulário de condição
  newCondition.value = {
    type: 'contact_has_tag',
    value: '',
    attribute: '',
    operator: 'equal_to',
  };
};

const removeCondition = index => {
  newStage.value.auto_create_conditions.splice(index, 1);
};

const getConditionTypeLabel = type => {
  const labels = {
    contact_has_tag: 'Contato tem tag',
    contact_has_custom_attribute: 'Atributo customizado',
    message_contains: 'Mensagem contém',
    conversation_has_priority: 'Prioridade da conversa',
    inbox_matches: 'Inbox específico',
  };
  return labels[type] || type;
};

const getOperatorLabel = operator => {
  const labels = {
    equal_to: 'Igual a',
    contains: 'Contém',
    not_equal_to: 'Diferente de',
  };
  return labels[operator] || operator;
};

// Funções para manipular campos personalizados globais
const globalCustomAttributes = computed({
  get: () => formData.value.global_custom_attributes,
  set: val => {
    formData.value.global_custom_attributes = val;
  },
});

function addGlobalCustomAttribute() {
  globalCustomAttributes.value.push({ name: '', type: '' });
}

function removeGlobalCustomAttribute(index) {
  globalCustomAttributes.value.splice(index, 1);
}

const handleSubmit = async () => {
  try {
    loading.value = true;
    errorMessage.value = '';

    // Verifica o limite de funis para versão não PRO
    if (!isStacklab && !props.isEditing) {
      const { data: existingFunnels } = await FunnelAPI.get();
      if (existingFunnels.length >= 2) {
        errorMessage.value = t('KANBAN.FUNNELS.FORM.ERROR_LIMIT_REACHED');
        return;
      }
    }

    // Preparar as etapas mantendo os templates existentes e atualizando posições
    const formattedStages = formatStagesForAPI();

    console.log('Estágios formatados para submit:', formattedStages);

    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      stages: formattedStages,
      agents: formData.value.agents,
      global_custom_attributes: formData.value.global_custom_attributes.filter(
        attr => attr.name.trim() !== '' && attr.type.trim() !== ''
      ),
    };

    let response;
    if (props.isEditing) {
      // Se estiver editando, mantém os dados existentes
      response = {
        data: {
          ...props.initialData,
          ...payload,
          stages: payload.stages, // Usa apenas as etapas atuais, removendo a mesclagem com as antigas
        },
      };
    } else {
      response = await FunnelAPI.create(payload);
    }

    if (response.data) {
      emit('saved', response.data);
    }
  } catch (error) {
    console.error('Erro ao salvar funil:', error);
    if (error.response?.data?.code === 'FUNNEL_LIMIT_REACHED') {
      errorMessage.value = error.response.data.error;
    } else {
      errorMessage.value = t('KANBAN.FUNNELS.FORM.ERROR_GENERIC');
    }
  } finally {
    loading.value = false;
  }
};

const startEditing = stage => {
  newStage.value = {
    id: stage.id,
    name: stage.name,
    color: stage.color,
    description: stage.description,
    auto_create_conditions: stage.auto_create_conditions || [],
  };
  editingStage.value = stage;
};

const saveStageEdit = index => {
  if (!editingStage.value) return;

  stages.value[index] = {
    ...stages.value[index],
    name: editingStage.value.name,
    description: editingStage.value.description,
    color: editingStage.value.color,
    auto_create_conditions: editingStage.value.auto_create_conditions,
  };

  editingStage.value = null;

  // Se estiver editando, atualiza imediatamente
  if (props.isEditing) {
    handleOrderChange();
  }
};

const cancelEditing = () => {
  editingStage.value = null;
  // Limpa o formulário de nova etapa
  newStage.value = {
    name: '',
    color: '#FF6B6B',
    description: '',
    auto_create_conditions: [],
  };
};

// Adicione um watch para debug
watch(
  () => formData.value.agents,
  newAgents => {
    console.log('Agentes atualizados:', newAgents);
  }
);

const toggleAgent = agent => {
  const index = formData.value.agents.findIndex(a => a.id === agent.id);
  if (index === -1) {
    formData.value.agents.push(agent);
  } else {
    formData.value.agents.splice(index, 1);
  }
};

// Adicionar watch para monitorar mudanças
watch(
  () => stages.value,
  newStages => {
    console.log('Etapas atualizadas:', newStages);
  },
  { deep: true }
);

watch(
  () => formData.value,
  newFormData => {
    console.log('FormData atualizado:', newFormData);
  },
  { deep: true }
);

watch(
  () => formData.value.global_custom_attributes,
  val => {
    if (!Array.isArray(val) || val.length === 0) {
      formData.value.global_custom_attributes = [{ name: '', type: '' }];
    }
  },
  { immediate: true }
);

const agentSearch = ref('');
const agentDropdownOpen = ref(false);
const agentSelectorRef = ref(null);
const filteredAgents = computed(() => {
  if (!agentSearch.value)
    return agentsList.value.filter(
      a => !formData.value.agents.some(sel => sel.id === a.id)
    );
  return agentsList.value.filter(
    a =>
      !formData.value.agents.some(sel => sel.id === a.id) &&
      a.name.toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});
function addAgent(agent) {
  if (!formData.value.agents.some(a => a.id === agent.id))
    formData.value.agents.push(agent);
  agentDropdownOpen.value = false;
  agentSearch.value = '';
}
function removeAgent(agent) {
  formData.value.agents = formData.value.agents.filter(a => a.id !== agent.id);
}
function closeAgentDropdown(e) {
  if (!agentSelectorRef.value?.contains(e.target)) {
    agentDropdownOpen.value = false;
    window.removeEventListener('click', closeAgentDropdown);
  }
}
function openAgentDropdown() {
  agentDropdownOpen.value = true;
  setTimeout(() => {
    window.removeEventListener('click', closeAgentDropdown);
    window.addEventListener('click', closeAgentDropdown);
  }, 0);
}

// Controle de tooltip de cópia por etapa
const copiedStageId = ref({});

// Função para copiar o id da etapa
async function copyStageId(id) {
  navigator.clipboard.writeText(id);
  copiedStageId.value[id] = true;
  await nextTick();
  setTimeout(() => {
    copiedStageId.value[id] = false;
  }, 1500);
}

// Controle de expansão/colapso das etapas na pré-visualização
</script>

<template>
  <form class="funnel-form" @submit.prevent="handleSubmit">
    <!-- Mensagem de erro -->
    <div
      v-if="errorMessage"
      class="mb-4 p-4 bg-ruby-50 dark:bg-ruby-900/20 border border-ruby-200 dark:border-ruby-800 rounded-lg"
    >
      <div class="flex items-center gap-2">
        <fluent-icon
          icon="error-circle"
          size="20"
          class="text-ruby-500 dark:text-ruby-300"
        />
        <span class="text-ruby-700 dark:text-ruby-200">{{ errorMessage }}</span>
      </div>
    </div>

    <div class="grid grid-cols-[1fr_400px] gap-6">
      <!-- Coluna Esquerda - Formulário -->
      <div class="space-y-6">
        <!-- Nome do Funil -->
        <div>
          <label class="block text-sm font-medium mb-2">
            {{ t('KANBAN.FUNNELS.FORM.NAME.LABEL') }}
          </label>
          <input
            v-model="formData.name"
            type="text"
            class="w-full px-3 py-2 border rounded-lg"
            :placeholder="t('KANBAN.FUNNELS.FORM.NAME.PLACEHOLDER')"
            required
          />
        </div>

        <!-- Descrição -->
        <div>
          <label class="block text-sm font-medium mb-2">
            {{ t('KANBAN.FUNNELS.FORM.DESCRIPTION.LABEL') }}
          </label>
          <textarea
            v-model="formData.description"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg"
            :placeholder="t('KANBAN.FUNNELS.FORM.DESCRIPTION.PLACEHOLDER')"
          />
        </div>

        <!-- Formulário Nova Etapa -->
        <div
          class="border border-slate-100 dark:border-slate-800 rounded-lg p-4 space-y-4"
          :class="{
            'border-2': editingStage,
          }"
          :style="editingStage ? { borderColor: newStage.color } : {}"
        >
          <h4 class="text-lg font-medium mb-4">
            <div class="flex items-center justify-between">
              <span>Etapas do Funil</span>
              <span
                v-if="editingStage"
                class="text-sm px-2 py-1 rounded"
                :style="{
                  backgroundColor: `${newStage.color}20`,
                  color: newStage.color,
                }"
              >
                Editando etapa
              </span>
            </div>
          </h4>
          <!-- Nome da Etapa -->
          <div>
            <label class="block text-sm font-medium mb-2">
              Nome da etapa
            </label>
            <input
              v-model="newStage.name"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              placeholder="Nome da etapa"
            />
          </div>
          <!-- Cor e Descrição em flex -->
          <div class="flex items-start gap-2">
            <div class="w-[180px] shrink-0">
              <label class="block text-sm font-medium mb-2"> Cor </label>
              <StageColorPicker v-model="newStage.color" />
            </div>
            <div class="flex-1 min-w-0">
              <label class="block text-sm font-medium mb-2"> Descrição </label>
              <input
                v-model="newStage.description"
                type="text"
                class="w-full px-3 py-2 border rounded-lg"
                placeholder="Descrição da etapa"
              />
            </div>
          </div>
          <!-- Condições de Auto Criação -->
          <div class="mt-4">
            <label class="block text-sm font-medium mb-2">
              Condições de Auto Criação
              <span class="text-xs text-slate-400 ml-1">
                (Deixe vazio para não criar automaticamente)
              </span>
            </label>

            <!-- Lista de condições existentes -->
            <div
              v-if="newStage.auto_create_conditions.length > 0"
              class="mb-3 space-y-2"
            >
              <div
                v-for="(condition, index) in newStage.auto_create_conditions"
                :key="index"
                class="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded border"
              >
                <span
                  class="text-xs px-2 py-1 bg-woot-100 dark:bg-woot-900 text-woot-700 dark:text-woot-300 rounded"
                >
                  {{ getConditionTypeLabel(condition.type) }}
                </span>
                <span
                  v-if="condition.attribute"
                  class="text-xs text-slate-600 dark:text-slate-400"
                >
                  {{ condition.attribute }}
                </span>
                <span
                  v-if="
                    condition.operator &&
                    condition.type === 'contact_has_custom_attribute'
                  "
                  class="text-xs text-slate-600 dark:text-slate-400"
                >
                  {{ getOperatorLabel(condition.operator) }}
                </span>
                <span class="text-xs font-medium">{{ condition.value }}</span>
                <button
                  type="button"
                  @click="removeCondition(index)"
                  class="ml-auto text-ruby-500 hover:text-ruby-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <!-- Formulário para adicionar nova condição -->
            <div class="flex items-center gap-2">
              <select
                v-model="newCondition.type"
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              >
                <option value="contact_has_tag">Contato tem tag</option>
                <option value="contact_has_custom_attribute">
                  Atributo customizado
                </option>
                <option value="message_contains">Mensagem contém</option>
                <option value="conversation_has_priority">
                  Prioridade da conversa
                </option>
                <option value="inbox_matches">Inbox específico</option>
              </select>
              <input
                v-if="newCondition.type === 'contact_has_custom_attribute'"
                v-model="newCondition.attribute"
                type="text"
                placeholder="Atributo"
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              />
              <select
                v-if="newCondition.type === 'contact_has_custom_attribute'"
                v-model="newCondition.operator"
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              >
                <option value="equal_to">Igual a</option>
                <option value="contains">Contém</option>
                <option value="not_equal_to">Diferente de</option>
              </select>
              <input
                v-model="newCondition.value"
                type="text"
                :placeholder="
                  newCondition.type === 'contact_has_tag'
                    ? 'Tag'
                    : newCondition.type === 'message_contains'
                    ? 'Texto'
                    : newCondition.type === 'conversation_has_priority'
                    ? 'Prioridade'
                    : newCondition.type === 'inbox_matches'
                    ? 'ID da inbox'
                    : 'Valor'
                "
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              />
              <Button
                color="blue"
                size="sm"
                :disabled="!newCondition.value"
                @click="addCondition"
                class="ml-2 flex items-center justify-center p-0 align-middle !h-10 !w-10 !min-w-[40px]"
                style="
                  height: 38px;
                  width: 40px;
                  min-width: 40px;
                  margin-top: -9px;
                "
              >
                <template #icon>
                  <fluent-icon icon="add" size="18" />
                </template>
              </Button>
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :disabled="!newStage.name"
              @click.stop="handleAddStage"
              :label="editingStage ? 'Salvar' : 'Adicionar etapa'"
            />
          </div>
        </div>

        <!-- Agentes do Funil -->
        <div
          class="border border-slate-100 dark:border-slate-800 rounded-lg p-4 space-y-4"
        >
          <h4 class="text-lg font-medium mb-4">Agentes do Funil</h4>
          <!-- Select com busca -->
          <div ref="agentSelectorRef" class="relative mb-2">
            <input
              v-model="agentSearch"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              placeholder="Buscar agente..."
              @focus="openAgentDropdown"
              @input="openAgentDropdown"
            />
            <div
              v-if="agentDropdownOpen"
              class="absolute left-0 top-full w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto z-[99999]"
            >
              <div
                v-for="agent in filteredAgents"
                :key="agent.id"
                class="px-3 py-2 hover:bg-woot-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2"
                @click="addAgent(agent)"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.avatar_url"
                  :size="24"
                  :status="agent.availability_status"
                />
                <span class="text-sm">{{ agent.name }}</span>
              </div>
              <div
                v-if="filteredAgents.length === 0"
                class="px-3 py-2 text-slate-400 text-sm"
              >
                Nenhum agente encontrado
              </div>
            </div>
          </div>
          <!-- Listagem compacta dos agentes selecionados -->
          <div v-if="formData.agents.length" class="space-y-1">
            <div
              class="flex items-center justify-between px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded-lg mb-2"
            >
              <span class="text-sm text-slate-600 dark:text-slate-300">
                {{ formData.agents.length }} agente{{
                  formData.agents.length !== 1 ? 's' : ''
                }}
                selecionado{{ formData.agents.length !== 1 ? 's' : '' }}
              </span>
              <button
                type="button"
                class="text-xs text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300"
                @click="formData.agents = []"
              >
                Limpar seleção
              </button>
            </div>
            <div
              v-for="agent in formData.agents"
              :key="agent.id"
              class="flex items-center gap-2 px-2 py-1 border border-slate-100 dark:border-slate-800 rounded mb-1"
            >
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url"
                :size="24"
                :status="agent.availability_status"
              />
              <span class="text-sm">{{ agent.name }}</span>
              <button
                type="button"
                class="ml-auto text-xs text-ruby-500 hover:text-ruby-600"
                @click="removeAgent(agent)"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        <!-- Campos Personalizados Globais -->
        <div
          class="border border-slate-100 dark:border-slate-800 rounded-lg p-4 space-y-4"
        >
          <h4 class="text-lg font-medium mb-4">
            Campos Personalizados Globais
          </h4>
          <div class="space-y-4">
            <div
              v-for="(attr, idx) in globalCustomAttributes || []"
              :key="idx"
              class="flex gap-2 items-center"
            >
              <input
                v-model="attr.name"
                type="text"
                class="w-1/2 rounded-lg"
                placeholder="Chave (name)"
              />
              <select v-model="attr.type" class="w-1/2 rounded-lg">
                <option value="" disabled selected>Tipo</option>
                <option value="string">Texto</option>
                <option value="number">Número</option>
                <option value="date">Data</option>
                <option value="boolean">Verdadeiro/Falso</option>
              </select>
              <button
                type="button"
                @click="removeGlobalCustomAttribute(idx)"
                class="p-1 text-ruby-500"
              >
                ✕
              </button>
            </div>
            <button
              type="button"
              @click="addGlobalCustomAttribute"
              class="mt-2 px-3 py-1 bg-woot-500 text-white rounded"
            >
              Adicionar campo
            </button>
          </div>
        </div>

        <!-- Botões -->
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="emit('close')"
          >
            {{ t('KANBAN.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            type="submit"
            :isLoading="loading"
            :disabled="!formData.name || !stages.length"
          >
            {{
              t(isEditing ? 'KANBAN.SAVE' : 'KANBAN.FUNNELS.FORM.CREATE.TITLE')
            }}
          </Button>
        </div>
      </div>

      <!-- Coluna Direita - Lista de Etapas -->
      <div
        class="border border-dashed border-slate-50 dark:border-slate-900 rounded-lg p-6"
      >
        <div class="sticky top-0">
          <h4 class="text-lg font-medium mb-4">Pré-visualização das etapas</h4>

          <!-- Lista de Etapas -->
          <div class="space-y-3">
            <draggable
              v-model="stages"
              :options="dragOptions"
              item-key="id"
              class="space-y-3"
              @change="handleOrderChange"
            >
              <template #item="{ element: stage, index }">
                <div
                  class="stage-preview-card flex flex-col border border-slate-100 dark:border-slate-800 rounded-lg cursor-move group transition-colors duration-200 bg-slate-100 dark:bg-slate-800"
                  @mouseenter="stage._hover = true"
                  @mouseleave="stage._hover = false"
                  :style="
                    stage._hover
                      ? {
                          background: `linear-gradient(90deg, ${stage.color}22 0%, transparent 100%)`,
                        }
                      : {}
                  "
                >
                  <div class="flex items-center gap-2 p-3 select-none">
                    <div
                      class="w-4 h-4 rounded-full"
                      :style="{ backgroundColor: stage.color }"
                    />
                    <div
                      class="font-medium flex-1 truncate"
                      :title="stage.name"
                    >
                      {{
                        stage.name.length > 25
                          ? stage.name.slice(0, 25) + '…'
                          : stage.name
                      }}
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="relative">
                        <button
                          type="button"
                          class="text-slate-400 hover:text-slate-500 p-0 m-0"
                          @click.stop="copyStageId(stage.id)"
                        >
                          <fluent-icon icon="copy" size="16" />
                        </button>
                        <span
                          v-if="copiedStageId[stage.id]"
                          class="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded bg-slate-700 text-white whitespace-nowrap z-10"
                          >Copiado!</span
                        >
                      </div>
                      <button
                        type="button"
                        class="text-xs text-slate-400 hover:text-slate-500 p-0 m-0"
                        @click.stop="startEditing(stage)"
                      >
                        <fluent-icon icon="edit" size="16" />
                      </button>
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-500 p-0 m-0"
                        @click.stop="removeStage(index)"
                      >
                        <fluent-icon icon="delete" size="16" />
                      </button>
                    </div>
                  </div>
                  <div
                    class="stage-preview-details p-0 max-h-0 opacity-0 overflow-hidden transition-all duration-200 ease-in-out group-hover:px-3 group-hover:pb-3 group-hover:opacity-100 group-hover:max-h-[500px] group-hover:pointer-events-auto pointer-events-none"
                  >
                    <div class="text-sm text-slate-600 mb-1">
                      {{ stage.description }}
                    </div>
                    <div
                      v-if="
                        stage.auto_create_conditions &&
                        stage.auto_create_conditions.length > 0
                      "
                      class="mt-1"
                    >
                      <div class="flex flex-wrap gap-1">
                        <span
                          v-for="(
                            condition, index
                          ) in stage.auto_create_conditions"
                          :key="index"
                          class="text-xs px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded"
                        >
                          {{ getConditionTypeLabel(condition.type) }}:
                          {{ condition.value }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>

            <!-- Empty State -->
            <div
              v-if="!stages.length"
              class="flex flex-col items-center justify-center py-8 text-slate-400"
            >
              <fluent-icon icon="list" size="32" class="mb-2" />
              <p class="text-sm">
                {{ t('KANBAN.FUNNELS.FORM.STAGES.EMPTY') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.border-woot {
  border-color: var(--color-woot);
  border-width: 1px;
}

.ghost-card {
  opacity: 0.5;
  background: #c8ebfb;
}

.cursor-move {
  cursor: move;
}

.loading-spinner {
  @apply w-6 h-6 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin;
}

.agent-card {
  @apply transition-all duration-200;

  &:hover {
    @apply transform scale-[1.02];
  }
}

// Oculta a scrollbar mas permite scroll
.scrollbar-hide {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Webkit */
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  max-height: 500px;
}
.stage-preview-card {
  position: relative;
}
.stage-preview-card {
  background: var(--tw-bg-opacity, 1) var(--tw-gradient, none);
}
.stage-preview-card:hover {
  background: var(--tw-gradient);
}
.stage-preview-details {
  /* Remove bg e gradiente daqui, fica só no card externo */
}
</style>
